/**
 * 统一功能日志 logger（PC 端 / renderer）
 *
 * 用法：
 *   import logger from '@/utils/logger'
 *   const log = logger.feat('WEBRTC_UPLOAD')
 *   log.entry('startUpload', { file })
 *   log.step('encoded chunk', { idx })
 *   log.call('httpRequest', { url })
 *   log.resp('httpRequest', { status })
 *   log.done('startUpload', { url })
 *   log.err('startUpload', err)
 *
 * 每条写入 ~/Library/Logs/AIIM/pc-YYYY-MM-DD.log + merged-YYYY-MM-DD.log（经 IPC 透传）。
 * 同时 console.log 以便 DevTools 可见。
 */

const SYMBOLS = {
    entry: '▶',
    step:  '⚙',
    call:  '→',
    resp:  '←',
    done:  '✓',
    err:   '✗',
}

function hasElectronAPI() {
    return typeof window !== 'undefined' && window.electronAPI && typeof window.electronAPI.invoke === 'function'
}

function safeStringify(v) {
    if (v == null) return v
    if (v instanceof Error) return { name: v.name, message: v.message, stack: v.stack }
    if (typeof v === 'object') {
        try { JSON.stringify(v); return v } catch (e) { return String(v) }
    }
    return v
}

function formatConsoleText(entry) {
    const head = `[${entry.feat}] ${entry.sym} ${entry.msg}`
    return entry.data === undefined ? head : head
}

function buildEntry(feat, kind, msg, data) {
    return {
        ts: new Date().toISOString(),
        src: 'pc',
        feat,
        lvl: kind === 'err' ? 'error' : 'info',
        sym: SYMBOLS[kind] || '',
        msg: msg == null ? '' : String(msg),
        data: data === undefined ? undefined : safeStringify(data),
    }
}

function renderLine(entry) {
    const dataStr = entry.data === undefined ? '' : ' ' + (typeof entry.data === 'string' ? entry.data : safeJson(entry.data))
    return `${entry.ts} [${entry.src}] [${entry.feat}] ${entry.sym} ${entry.msg}${dataStr}\n`
}

function safeJson(v) {
    try { return JSON.stringify(v) } catch (e) { return String(v) }
}

// 主进程未就绪时的缓存，限 1000 条
const PENDING_MAX = 1000
let pending = []
let flushed = false

function flushPending() {
    if (flushed) return
    flushed = true
    const items = pending
    pending = []
    for (const line of items) {
        try { window.electronAPI.invoke('append-log', { source: 'pc', line }) } catch (e) { /* ignore */ }
    }
}

function dispatch(entry) {
    const line = renderLine(entry)
    const consoleText = formatConsoleText(entry)
    const printer = entry.lvl === 'error' ? console.error : console.log
    try {
        if (entry.data === undefined) printer(consoleText)
        else printer(consoleText, entry.data)
    } catch (e) { /* ignore */ }
    if (!hasElectronAPI()) {
        // web / 未注入 electronAPI 时，尽量不丢：先缓存
        if (pending.length < PENDING_MAX) pending.push(line)
        return
    }
    if (pending.length) flushPending()
    try { window.electronAPI.invoke('append-log', { source: 'pc', line }) } catch (e) { /* ignore */ }
}

function featLogger(featName) {
    const feat = String(featName || 'DEFAULT').toUpperCase()
    const make = (kind) => (msg, data) => dispatch(buildEntry(feat, kind, msg, data))
    return {
        entry: make('entry'),
        step:  make('step'),
        call:  make('call'),
        resp:  make('resp'),
        done:  make('done'),
        err:   make('err'),
    }
}

// 可选：轻量 console 钩子，识别 `[XXX] ...` 形式的调用并补上前缀日志
function installConsolePrefixForwarder() {
    if (installConsolePrefixForwarder._installed) return
    installConsolePrefixForwarder._installed = true
    const origLog = console.log.bind(console)
    const origWarn = console.warn.bind(console)
    const origErr = console.error.bind(console)
    const PREFIX_RE = /^\[([A-Z0-9_]+)\]\s*/
    // VIDEO_HLS 已由 videoHlsLogger.js 经 append-video-hls-log IPC 写入统一日志，这里跳过避免重复
    const SKIP_FEATS = new Set(['VIDEO_HLS'])
    const scan = (level, args) => {
        try {
            const first = args && args[0]
            if (typeof first !== 'string') return
            const m = first.match(PREFIX_RE)
            if (!m) return
            const feat = m[1]
            if (SKIP_FEATS.has(feat)) return
            const rest = first.replace(PREFIX_RE, '')
            const data = args.length > 1 ? args.slice(1) : undefined
            const kind = level === 'error' ? 'err' : 'step'
            const entry = buildEntry(feat, kind, rest, data)
            const line = renderLine(entry)
            if (hasElectronAPI()) {
                try { window.electronAPI.invoke('append-log', { source: 'pc', line }) } catch (e) { /* ignore */ }
            } else if (pending.length < PENDING_MAX) {
                pending.push(line)
            }
        } catch (e) { /* ignore */ }
    }
    console.log = function (...args) { origLog(...args); scan('log', args) }
    console.warn = function (...args) { origWarn(...args); scan('warn', args) }
    console.error = function (...args) { origErr(...args); scan('error', args) }
}

const logger = {
    feat: featLogger,
    installConsolePrefixForwarder,
}

export default logger
export { featLogger as feat, installConsolePrefixForwarder }

if (typeof window !== 'undefined') {
    window.__logger = logger
}
